import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useCallback } from 'react';

import { getBarberByIdOrRouteQuery } from '@/api/booking/getBarberByIdOrRouteQuery';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { acceptWaitlistSlotMutation } from '@/api/waitlist/acceptWaitlistSlotMutation';
import { updateWaitlistTipMutation } from '@/api/waitlist/updateWaitlistTipMutation';
import { waitlistDetailQueryOptions } from '@/api/waitlist/waitlistDetailQuery';
import type { WaitlistStatus } from '@/api/waitlist/waitlistDetailQuery/types';
import { waitlistTimeSlotsQueryOptions } from '@/api/waitlist/waitlistTimeSlotsQuery';
import { toast } from '@/components/_shared/Toaster';
import type { Tip } from '@/components/booking/TipSelector/types';
import { routes } from '@/constants/routes';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { DateFormat, formatDate } from '@/utils/dateUtils';

import { getEffectiveWaitlistUiState } from './getEffectiveWaitlistUiState';

const WAITLIST_POLLING_INTERVAL_MS = 30_000;

export const useViewWaitlistData = ({
  waitlistCustomerId,
  slotIdFromUrl,
  selectedSlotId,
}: {
  waitlistCustomerId: string;
  slotIdFromUrl?: string;
  selectedSlotId: string | null;
}) => {
  const { t } = useScreenTranslations('ViewWaitlistContentWrapper');
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: waitlistData } = useSuspenseQuery({
    ...waitlistDetailQueryOptions(waitlistCustomerId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === null ? WAITLIST_POLLING_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });

  const { data: shop } = useSuspenseQuery(
    shopDetailQuery(waitlistData.shopId, {
      include: ['address', 'staticMap', 'photos'],
    }),
  );

  const { data: professionalData } = useSuspenseQuery(
    getBarberByIdOrRouteQuery(
      waitlistData.shopId,
      waitlistData.barberId || 'any',
      ['photos'],
    ),
  );

  const attemptId = slotIdFromUrl ?? waitlistData.attemptId ?? null;

  const { data: timeSlotsData } = useQuery(
    waitlistTimeSlotsQueryOptions(waitlistCustomerId, attemptId),
  );

  const barber = professionalData.barber;
  const customerName = waitlistData.customer?.firstName || '';
  const barberName = `${barber.firstName} ${barber.lastName ? barber.lastName.charAt(0) + '.' : ''}`;
  const barberPhotoUrl = barber?.photos?.[0]?.url;

  const slotTimes = timeSlotsData?.times ?? null;
  const slotTimesForState =
    slotTimes?.map((s) => ({
      id: s.waitingListSlotId,
      slotId: s.waitingListSlotId,
    })) ?? null;
  const uiState = getEffectiveWaitlistUiState({
    status: waitlistData.status as WaitlistStatus,
    attemptId,
    slotId: selectedSlotId,
    slotTimes: slotTimesForState,
  });
  const selectedSlot = slotTimes?.find(
    (s) => s.waitingListSlotId === selectedSlotId,
  );

  // Use formatDate (not formatDateWithTimezone) because `day` is a calendar date
  // (e.g. "2026-03-20T00:00:00.000Z"), not a point in time. Timezone-shifting
  // midnight UTC would roll it back to the previous day in western timezones.
  const formattedDate = formatDate(
    waitlistData.day.split('T')[0],
    DateFormat.CART_ITEM_DATE,
  );

  const { mutate: acceptSlot, isPending: isAcceptingSlot } = useMutation(
    acceptWaitlistSlotMutation({
      onSuccess: (data) => {
        router.replace(
          routes.viewReservation({
            saleOrderId: data.saleOrderId ?? data.id,
          }),
        );
      },
      onError: () => {
        toast({ variant: 'error', message: t('errors.bookingFailed') });
      },
    }),
  );

  const { mutate: updateTip, isPending: isUpdatingTip } = useMutation(
    updateWaitlistTipMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['waitlistDetail', waitlistCustomerId],
        });
      },
    }),
  );

  const isBooking = isAcceptingSlot;

  // Tipping is gated on the barber, not the shop (matches water-on-web).
  // "Any barber" waitlist entries have no specific barber, so tipping is allowed.
  const canAddTip = !waitlistData.barberId || barber.earlyTipping === true;
  const availableTips: Tip[] = waitlistData.availableTips ?? [];

  const handleSelectTip = useCallback(
    (tip?: Tip | { amount: number; isCustom: true }) => {
      const newTipAmount = tip?.amount ?? 0;
      updateTip({
        waitlistCustomerId,
        tipAmount: newTipAmount,
      });
    },
    [updateTip, waitlistCustomerId],
  );

  const handleBook = useCallback(() => {
    if (uiState === 'selected' && selectedSlotId) {
      acceptSlot({
        waitingListCustomerId: waitlistCustomerId,
        waitingListSlotId: selectedSlotId,
      });
    }
  }, [uiState, selectedSlotId, acceptSlot, waitlistCustomerId]);

  return {
    waitlistData,
    shop,
    barber,
    customerName,
    barberName,
    barberPhotoUrl,
    slotTimes,
    uiState,
    selectedSlot,
    formattedDate,
    availableTips,
    canAddTip,
    isBooking,
    isUpdatingTip,
    queryClient,
    handleSelectTip,
    handleBook,
  };
};
